import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Layers,
  Lock,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import TextReportDialog from "@/components/TextReportDialog";
import {
  OutlineButton,
  PrimaryButton,
  selectCls,
} from "@/components/masters/UI";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv, toCsv } from "@/lib/csv";
import { formatPublishedBy } from "@/lib/audit";

const WARNING = "#F2994A";

const AllocationPage = () => {
  const { sid: routeSid } = useParams();
  const navigate = useNavigate();

  const [sid, setSid] = useState(routeSid || null);
  const [resolving, setResolving] = useState(!routeSid);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingRow, setSavingRow] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [fetchingReport, setFetchingReport] = useState(false);

  // If we landed on /allocation with no sid, resolve today's session for the
  // Supervisor's Location. Server already restricts /attendance/sessions to
  // the caller's Location, so no cross-Location leak is possible here.
  useEffect(() => {
    if (routeSid) {
      setSid(routeSid);
      setResolving(false);
      return;
    }
    setResolving(true);
    const today = new Date();
    const y = today.getFullYear();
    const m = `${today.getMonth() + 1}`.padStart(2, "0");
    const d = `${today.getDate()}`.padStart(2, "0");
    api
      .get("/attendance/sessions", { params: { date: `${y}-${m}-${d}` } })
      .then((r) => {
        const sessions = r.data || [];
        // Prefer the most-recent scan_in_closed session (ready for review).
        const closed = sessions.find((s) => s.status === "scan_in_closed");
        const picked = closed || sessions[0] || null;
        setSid(picked ? picked.id : null);
      })
      .catch(() => setSid(null))
      .finally(() => setResolving(false));
  }, [routeSid]);

  const load = useCallback(async () => {
    if (!sid) {
      setPayload(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/allocations/session/${sid}`);
      setPayload(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 404) {
        toast.error(formatApiError(e));
        navigate("/allocations");
        return;
      }
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sid, navigate]);

  useEffect(() => {
    if (!resolving) load();
  }, [load, resolving]);

  const scanInClosed = !!payload?.scan_in_closed;
  const session = payload?.session;
  const allocation = payload?.allocation;
  const rows = payload?.rows || [];
  const departments = payload?.departments || [];
  const noSession = !resolving && !sid;

  // Live department counts as the Supervisor edits (Capacity ref only).
  const deptCounts = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      if (!r.department_id) continue;
      acc[r.department_id] = (acc[r.department_id] || 0) + 1;
    }
    return acc;
  }, [rows]);

  const deptById = useMemo(() => {
    const m = {};
    for (const d of departments) m[d.id] = d;
    return m;
  }, [departments]);

  const updateRowDept = async (rowId, department_id) => {
    setSavingRow(rowId);
    try {
      const { data } = await api.patch(`/allocations/rows/${rowId}`, {
        department_id,
      });
      setPayload((p) => ({
        ...p,
        allocation: p.allocation
          ? { ...p.allocation, can_copy_text: false }
          : p.allocation,
        rows: p.rows.map((r) => (r.id === rowId ? data : r)),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingRow(null);
    }
  };

  const publishAll = async () => {
    if (!allocation) return;
    setPublishing(true);
    try {
      const { data } = await api.post(
        `/allocations/${allocation.id}/publish`
      );
      setPayload((p) => ({ ...p, allocation: data.allocation, rows: data.rows }));
      toast.success(
        `Published ${data.confirmed_rows} row${data.confirmed_rows === 1 ? "" : "s"}`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublishing(false);
    }
  };

  const exportRows = () => {
    if (!session || rows.length === 0) return;
    const csv = toCsv(rows, [
      { header: "Employee ID", get: (r) => r.employee_id },
      { header: "Name", get: (r) => r.name },
      { header: "Department", get: (r) => r.department_name || "" },
      {
        header: "Status",
        get: (r) => (r.status === "confirmed" ? "Confirmed" : "Draft"),
      },
    ]);
    downloadCsv(
      `allocation-${session.date}-${(session.shift_name || "").replace(/\s+/g, "_")}`,
      csv
    );
    toast.success(`Exported ${rows.length} rows`);
  };

  const canCopyText = allocation?.can_copy_text !== undefined
    ? Boolean(allocation.can_copy_text)
    : (allocation?.status === "confirmed" && rows.length > 0 && rows.every((r) => r.status === "confirmed"));

  const handleOpenTextReport = async () => {
    if (!allocation || !canCopyText) {
      toast.error("Publish the allocation first");
      return;
    }
    setFetchingReport(true);
    try {
      const { data } = await api.get(`/allocations/${allocation.id}/text-report`);
      setReportText(data.text || "");
      setReportOpen(true);
    } catch (e) {
      if (e?.response?.status === 409) {
        setPayload((p) => ({
          ...p,
          allocation: p.allocation
            ? { ...p.allocation, can_copy_text: false }
            : p.allocation,
        }));
      }
      toast.error(formatApiError(e));
    } finally {
      setFetchingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="allocation-page">
      <AppHeader />

      <PageShell
        eyebrow="Department Allocation"
        title={
          session
            ? `Session · ${session.date} · ${session.shift_name}`
            : "Allocation review"
        }
        subtitle={
          allocation
            ? `Allocation status: ${allocation.status === "confirmed" ? "Confirmed" : "Draft"} · ${rows.length} row${rows.length === 1 ? "" : "s"}${
                formatPublishedBy(allocation) ? ` · ${formatPublishedBy(allocation)}` : ""
              }`
            : "Review each scanned employee's department and publish the allocation once."
        }
        primaryAction={
          <div className="flex items-center gap-2">
            <span
              title={canCopyText ? undefined : "Publish the allocation first"}
              className="inline-block"
            >
              <OutlineButton
                testId="allocation-copy-text"
                disabled={!canCopyText || fetchingReport}
                title={canCopyText ? "Copy as text" : "Publish the allocation first"}
                onClick={handleOpenTextReport}
              >
                <Copy className="mr-1 inline h-4 w-4" />
                {fetchingReport ? "Loading…" : "Copy as text"}
              </OutlineButton>
            </span>
            {scanInClosed && rows.length > 0 && (
              <OutlineButton testId="allocation-export" onClick={exportRows}>
                <Download className="mr-1 inline h-4 w-4" />
                Export
              </OutlineButton>
            )}
            <OutlineButton
              testId="allocation-back"
              onClick={() => navigate("/allocations")}
            >
              <ArrowLeft className="mr-1 inline h-4 w-4" />
              Back to Allocations
            </OutlineButton>
          </div>
        }
        testId="allocation-shell"
      >
        {resolving || loading ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="allocation-loading"
          >
            Loading allocation…
          </div>
        ) : error ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm shadow-card"
            style={{
              borderColor: "rgba(231, 76, 60, 0.3)",
              color: "var(--vision-error)",
            }}
            data-testid="allocation-error"
          >
            {error}
          </div>
        ) : noSession || !scanInClosed ? (
          <ScanIncomplete />
        ) : (
          <>
            <RowsTable
              rows={rows}
              departments={departments}
              deptById={deptById}
              deptCounts={deptCounts}
              savingRow={savingRow}
              onUpdate={updateRowDept}
            />
            <PublishBar
              rows={rows}
              allocation={allocation}
              publishing={publishing}
              onPublish={publishAll}
            />
          </>
        )}
        <TextReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          text={reportText}
        />
      </PageShell>
    </div>
  );
};

// ------------------------------------------------------------------ Scan gate

const ScanIncomplete = () => (
  <div
    className="flex flex-col items-center gap-3 rounded-2xl border bg-white p-12 text-center shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.10)" }}
    data-testid="allocation-scan-incomplete"
  >
    <div
      className="flex h-12 w-12 items-center justify-center rounded-full"
      style={{
        backgroundColor: "rgba(52, 73, 94, 0.10)",
        color: "var(--vision-slate)",
      }}
    >
      <Lock className="h-6 w-6" />
    </div>
    <div className="font-display text-lg font-bold text-vision-ink">
      Scanning not completed.
    </div>
  </div>
);

// ------------------------------------------------------------------ Rows table

const RowsTable = ({
  rows,
  departments,
  deptById,
  deptCounts,
  savingRow,
  onUpdate,
}) => {
  if (rows.length === 0) {
    return (
      <div
        className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
        style={{ borderColor: "rgba(52,73,94,0.08)" }}
        data-testid="allocation-empty"
      >
        No employees were scanned in for this session.
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-2xl border bg-white shadow-card"
      style={{ borderColor: "rgba(52,73,94,0.08)" }}
      data-testid="allocation-rows"
    >
      <div
        className="hidden grid-cols-[1.4fr_1.2fr_1.6fr_140px_130px] gap-4 border-b bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid"
        style={{ borderColor: "rgba(52,73,94,0.08)" }}
      >
        <div>Employee</div>
        <div>Employee ID</div>
        <div>Department</div>
        <div>Load</div>
        <div>Status</div>
      </div>

      {rows.map((r) => {
        const dep = deptById[r.department_id];
        const filled = deptCounts[r.department_id] || 0;
        const cap = dep?.capacity ?? null;
        const over = cap != null && filled > cap;
        const confirmed = r.status === "confirmed";
        const saving = savingRow === r.id;
        return (
          <div
            key={r.id}
            data-testid={`allocation-row-${r.id}`}
            className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1.4fr_1.2fr_1.6fr_140px_130px] md:gap-4"
            style={{ borderColor: "rgba(52,73,94,0.06)" }}
          >
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-bold text-vision-ink">
                {r.name}
              </div>
            </div>
            <div className="text-sm text-vision-slate">{r.employee_id}</div>

            <div>
              <select
                className={selectCls}
                data-testid={`allocation-row-${r.id}-dept`}
                value={r.department_id || ""}
                disabled={saving}
                onChange={(e) => onUpdate(r.id, e.target.value)}
              >
                {!deptById[r.department_id] && r.department_id && (
                  <option value={r.department_id}>
                    {r.department_name} (inactive)
                  </option>
                )}
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} · cap {d.capacity}
                  </option>
                ))}
              </select>
            </div>

            <CapacityChip filled={filled} cap={cap} over={over} rowId={r.id} />

            <StatusChip confirmed={confirmed} rowId={r.id} />
          </div>
        );
      })}
    </div>
  );
};

const CapacityChip = ({ filled, cap, over, rowId }) => (
  <div className="flex items-center gap-2">
    <span
      className="chip"
      data-testid={`allocation-row-${rowId}-load`}
      style={
        over
          ? {
              backgroundColor: "rgba(242, 153, 74, 0.16)",
              color: "#7a3f0a",
              border: `1px solid ${WARNING}`,
            }
          : {
              backgroundColor: "rgba(52, 73, 94, 0.08)",
              color: "var(--vision-slate)",
              border: "1px solid rgba(52, 73, 94, 0.15)",
            }
      }
    >
      <Layers className="h-3.5 w-3.5" />
      {filled} / {cap ?? "—"}
    </span>
    {over && (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-semibold"
        style={{ color: WARNING }}
        data-testid={`allocation-row-${rowId}-over`}
        title="Over capacity — reference only, publish still allowed."
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        Over
      </span>
    )}
  </div>
);

const StatusChip = ({ confirmed, rowId }) => (
  <span
    className="chip"
    data-testid={`allocation-row-${rowId}-status`}
    style={
      confirmed
        ? {
            backgroundColor: "rgba(39, 174, 96, 0.14)",
            color: "#1e8a4a",
            border: "1px solid rgba(39, 174, 96, 0.35)",
          }
        : {
            backgroundColor: "rgba(0, 53, 158, 0.10)",
            color: "var(--vision-navy)",
            border: "1px solid rgba(0, 53, 158, 0.25)",
          }
    }
  >
    <span
      className="inline-block h-1.5 w-1.5 rounded-full"
      style={{
        backgroundColor: confirmed
          ? "var(--vision-success)"
          : "var(--vision-navy)",
      }}
    />
    {confirmed ? "Confirmed" : "Draft"}
  </span>
);

// ------------------------------------------------------------------ Publish bar

const PublishBar = ({ rows, allocation, publishing, onPublish }) => {
  const total = rows.length;
  const confirmed = rows.filter((r) => r.status === "confirmed").length;
  const canPublish = total > 0 && !!allocation;
  return (
    <div
      className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border bg-white p-5 shadow-card"
      style={{ borderColor: "rgba(52,73,94,0.08)" }}
      data-testid="allocation-publish-bar"
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{
            backgroundColor: "rgba(39, 174, 96, 0.14)",
            color: "#1e8a4a",
          }}
        >
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-sm font-bold text-vision-ink">
            {confirmed} of {total} confirmed
          </div>
          <div className="text-xs text-vision-slate/60">
            Publish All flips every row Draft → Confirmed in one go. You can
            publish again after more scans.
          </div>
          {formatPublishedBy(allocation) && (
            <div
              className="mt-1 text-xs font-semibold text-vision-navy"
              data-testid="allocation-published-by"
            >
              {formatPublishedBy(allocation)}
            </div>
          )}
        </div>
      </div>
      <PrimaryButton
        testId="allocation-publish-all"
        onClick={onPublish}
        disabled={!canPublish || publishing}
      >
        {publishing ? "Publishing…" : "Publish All"}
      </PrimaryButton>
    </div>
  );
};

export default AllocationPage;
